/**
 * Login — RCA Tool
 * Three sign-in paths: email/password, Google (Supabase OAuth), Microsoft (Supabase OAuth).
 */

import { useState } from 'react'
import { useNavigate, useLocation } from 'react-router-dom'
import { useForm } from 'react-hook-form'
import { zodResolver } from '@hookform/resolvers/zod'
import { z } from 'zod'
import { Loader2 } from 'lucide-react'

import { useAuth } from '@/context/AuthContext'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Card, CardContent, CardFooter, CardHeader, CardTitle } from '@/components/ui/card'
import { Form, FormControl, FormField, FormItem, FormLabel, FormMessage } from '@/components/ui/form'

const loginSchema = z.object({
  email: z.string().email('Enter a valid email address.'),
  password: z.string().min(1, 'Password is required.'),
})
type LoginFormValues = z.infer<typeof loginSchema>

function GoogleIcon() {
  return (
    <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" className="h-4 w-4 mr-2 flex-shrink-0" aria-hidden="true">
      <path d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z" fill="#4285F4"/>
      <path d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z" fill="#34A853"/>
      <path d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l3.66-2.84z" fill="#FBBC05"/>
      <path d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z" fill="#EA4335"/>
    </svg>
  )
}

function MicrosoftIcon() {
  return (
    <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 21 21" className="h-4 w-4 mr-2 flex-shrink-0" aria-hidden="true">
      <rect x="1" y="1" width="9" height="9" fill="#f25022"/>
      <rect x="11" y="1" width="9" height="9" fill="#7fba00"/>
      <rect x="1" y="11" width="9" height="9" fill="#00a4ef"/>
      <rect x="11" y="11" width="9" height="9" fill="#ffb900"/>
    </svg>
  )
}

export default function Login() {
  const { login, loginWithGoogle, loginWithMicrosoft } = useAuth()
  const navigate = useNavigate()
  const location = useLocation()
  const from = (location.state as { from?: { pathname: string } })?.from?.pathname ?? '/dashboard'

  const [globalError, setGlobalError] = useState<string | null>(null)
  const [googleLoading, setGoogleLoading] = useState(false)
  const [msLoading, setMsLoading] = useState(false)

  const form = useForm<LoginFormValues>({
    resolver: zodResolver(loginSchema),
    defaultValues: { email: '', password: '' },
  })

  const { isSubmitting } = form.formState
  const isAnyLoading = isSubmitting || googleLoading || msLoading

  async function onSubmit(values: LoginFormValues) {
    setGlobalError(null)
    try {
      await login(values.email, values.password)
      navigate(from, { replace: true })
    } catch (err) {
      setGlobalError(err instanceof Error ? err.message : 'Sign-in failed.')
    }
  }

  async function handleGoogle() {
    setGlobalError(null)
    setGoogleLoading(true)
    try {
      await loginWithGoogle()
      // Supabase redirects the page — this line only reached on error
    } catch (err) {
      setGlobalError(err instanceof Error ? err.message : 'Google sign-in failed.')
      setGoogleLoading(false)
    }
  }

  async function handleMicrosoft() {
    setGlobalError(null)
    setMsLoading(true)
    try {
      await loginWithMicrosoft()
      // Supabase redirects the page — this line only reached on error
    } catch (err) {
      setGlobalError(err instanceof Error ? err.message : 'Microsoft sign-in failed.')
      setMsLoading(false)
    }
  }

  return (
    <div className="relative flex min-h-screen items-center justify-center overflow-hidden bg-slate-50 px-4">
      <div
        className="pointer-events-none absolute inset-0 opacity-40"
        style={{
          backgroundImage: 'radial-gradient(circle at 1px 1px, #cbd5e1 1px, transparent 0)',
          backgroundSize: '32px 32px',
        }}
      />
      <div className="pointer-events-none absolute inset-0 bg-gradient-to-br from-blue-50/60 via-transparent to-indigo-100/40" />

      <div className="relative z-10 w-full max-w-md">
        <div className="mb-8 flex justify-center">
          <img
            src="/logo-primary.svg"
            alt="RCA Tool"
            className="h-16 w-auto"
            onError={(e) => { (e.currentTarget as HTMLImageElement).style.display = 'none' }}
          />
        </div>

        <Card className="border border-slate-200 shadow-xl shadow-slate-200/60">
          <CardHeader className="space-y-1 pb-4">
            <CardTitle className="text-center text-2xl font-bold tracking-tight text-slate-900">
              Risk &amp; Complexity Assessment
            </CardTitle>
            <p className="text-center text-sm text-slate-500">Sign in to continue</p>
          </CardHeader>

          <CardContent className="space-y-5">
            {globalError && (
              <div role="alert" className="rounded-md border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-700">
                {globalError}
              </div>
            )}

            <Form {...form}>
              <form onSubmit={form.handleSubmit(onSubmit)} noValidate className="space-y-4">
                <FormField control={form.control} name="email" render={({ field }) => (
                  <FormItem>
                    <FormLabel>Email</FormLabel>
                    <FormControl>
                      <Input type="email" placeholder="you@example.com" autoComplete="email" disabled={isAnyLoading} {...field} />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )} />

                <FormField control={form.control} name="password" render={({ field }) => (
                  <FormItem>
                    <FormLabel>Password</FormLabel>
                    <FormControl>
                      <Input type="password" autoComplete="current-password" disabled={isAnyLoading} {...field} />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )} />

                <Button type="submit" className="w-full" disabled={isAnyLoading}>
                  {isSubmitting ? <><Loader2 className="mr-2 h-4 w-4 animate-spin" />Signing in…</> : 'Sign In'}
                </Button>
              </form>
            </Form>

            <div className="relative flex items-center gap-3">
              <div className="h-px flex-1 bg-slate-200" />
              <span className="text-xs text-slate-400">or</span>
              <div className="h-px flex-1 bg-slate-200" />
            </div>

            <Button type="button" variant="outline" className="w-full" onClick={handleGoogle} disabled={isAnyLoading}>
              {googleLoading
                ? <><Loader2 className="mr-2 h-4 w-4 animate-spin" />Connecting to Google…</>
                : <><GoogleIcon />Sign in with Google</>
              }
            </Button>

            <Button type="button" variant="outline" className="w-full" onClick={handleMicrosoft} disabled={isAnyLoading}>
              {msLoading
                ? <><Loader2 className="mr-2 h-4 w-4 animate-spin" />Connecting to Microsoft…</>
                : <><MicrosoftIcon />Sign in with Microsoft</>
              }
            </Button>
          </CardContent>

          <CardFooter className="justify-center pb-6">
            <p className="text-xs text-slate-400">Risk &amp; Complexity Assessment Tool</p>
          </CardFooter>
        </Card>
      </div>
    </div>
  )
}
